import { Button, Dropdown, Layout, Menu, Drawer } from 'antd';
import { useState, useEffect } from 'react';
import './layout.css'
import {
    MenuOutlined,
    HistoryOutlined,
} from "@ant-design/icons";
import Flex from "../../Flex";
import Logo from "./Logo";
import ProfileDropdown from "./ProfileDropdown";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";
import { Route, useHistory } from "react-router-dom";
import { PERMISSIONS, useUser } from "../../../context/UserContext";
import { useIntroModal } from "../../../context/IntroModalContext";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import { CalendarOutlined, TableOutlined } from "@ant-design/icons";
import { GiMedicalPack } from "react-icons/gi";
import { FaClinicMedical, FaCashRegister } from "react-icons/fa";
import { AiOutlinePlus, AiOutlineApartment, AiOutlineAreaChart, AiFillDatabase } from "react-icons/ai";
import { BsFillCalendarDateFill, BsCashStack, BsFillPersonBadgeFill } from 'react-icons/bs';
import { HiUserGroup } from 'react-icons/hi';
import NotificationMenu from "../../../../app/NotificationMenu";
import AiChatButton from "../../../../app/ai-chat/AiChatButton";

const { Header } = Layout;

const { SubMenu } = Menu;

const Icon = ({ icon }) => {
    return (
        <div className={'anticon antitem'}>
            {icon}
        </div>
    )
}

const DefinitionsIcon = () => {
    return <Icon icon={<AiFillDatabase />} />
}

const DashboardIcon = () => {
    return <Icon icon={<AiOutlineAreaChart />} />
}

const CashRegisterIcon = () => {
    return <Icon icon={<FaCashRegister />} />
}

const DoctorIcon = () => {
    return <Icon icon={<GiMedicalPack />} />
}

const UnitIcon = () => {
    return <Icon icon={<AiOutlineApartment />} />
}

const OperationIcon = () => {
    return <Icon icon={<AiOutlinePlus />} />
}

const InstitutionIcon = () => {
    return <Icon icon={<FaClinicMedical />} />
}

const NewAppointmentIcon = () => {
    return <Icon icon={<BsFillCalendarDateFill />} />
}

const Expense = () => {
    return <Icon icon={<BsCashStack />} />
}

const Patient = () => {
    return <Icon icon={<BsFillPersonBadgeFill />} />
}

const UserIcon = () => {
    return <Icon icon={<HiUserGroup />} />
}

export default function MainLayout({ children }) {
    const { t } = useTranslation();
    const history = useHistory();
    const key = window.location.pathname;
    const { hasPermission } = useUser()
    const { missedVersions, isLoading } = useIntroModal();
    const [menuVisible, setMenuVisible] = useState(false);

    // Get theme settings
    const theme = window.AppSettings?.theme || {};
    const headerColor = theme.headerColor || '#34495e';

    // Simple helper to check if color is dark to set text color
    const isDark = (color) => {
        if (!color || color.startsWith('rgba') || color === 'transparent') return false;
        const c = color.substring(1);      // strip #
        const rgb = parseInt(c, 16);   // convert rrggbb to decimal
        const r = (rgb >> 16) & 0xff;  // extract red
        const g = (rgb >> 8) & 0xff;  // extract green
        const b = (rgb >> 0) & 0xff;  // extract blue
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
        return luma < 128;
    }

    const textColor = isDark(headerColor) ? '#ffffff' : '#595959';
    const menuTheme = isDark(headerColor) ? 'dark' : 'light';

    useEffect(() => {
        if (!isLoading && missedVersions && missedVersions.length > 0) {
            history.push('/app/changelog');
        }
    }, [isLoading, missedVersions, history]);

    const getMenuItem = (key, text) => {
        return (
            <Menu.Item key={key} icon={<CalendarOutlined />}>
                <a href={key}>{text}</a>
            </Menu.Item>
        )
    }

    const hasSystemPermission = () => {
        return hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_OPERATION_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) ||
            hasPermission(PERMISSIONS.FIRM_USER_MANAGER) ||
            hasPermission(PERMISSIONS.FIRM_USER_MANAGER) ||
            hasPermission(PERMISSIONS.APPLOG_VIEW) ||
            hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING)
    }

    const getMenu = () => {
        return (
            <>
                {hasPermission(PERMISSIONS.DASHBOARD) && <Menu.Item key={'/app/dashboard'}>
                    <Menu.Item key={`/app/dashboard`} icon={<DashboardIcon />}>{t('Dashboard')}</Menu.Item>
                </Menu.Item>}

                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/calendar`} icon={<CalendarOutlined />}>{t('Calendar')}</Menu.Item>}
                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/appointments/grid`} icon={<TableOutlined />}>{t('Appointments')}</Menu.Item>}
                {hasPermission(PERMISSIONS.APPOINTMENT_VIEW) && <Menu.Item key={`/app/patients/grid`} icon={<Patient />}>{t('Patients')}</Menu.Item>}
                {hasPermission(PERMISSIONS.EXPENSE_VIEW) && <Menu.Item key={`/app/expenses`} icon={<Expense />}>{t('Expenses')}</Menu.Item>}
                {hasPermission(PERMISSIONS.CASH_REGISTER_VIEW) && <Menu.Item key={`/app/cash-view`} icon={<CashRegisterIcon />}>{t('Cash Register')}</Menu.Item>}
                <Menu.Item key={`/app/system/operations`} icon={<OperationIcon />}>{t('Operations')}</Menu.Item>
                <Menu.Item key={`/app/system/institutions`} icon={<InstitutionIcon />}>{t('Institutions')}</Menu.Item>

                {
                    hasSystemPermission() && (
                        <SubMenu key={'system'} title={t('System')} icon={<DefinitionsIcon />}>
                            {hasPermission(PERMISSIONS.FIRM_UNIT_MANAGER) && <Menu.Item key={`/app/system/units`} icon={<UnitIcon />}>{t('Units')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_DOCTOR_MANAGER) && <Menu.Item key={`/app/system/doctors`} icon={<DoctorIcon />}>{t('Doctors')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) && <Menu.Item key={`/app/system/cash-registers`} icon={<CashRegisterIcon />}>{t('Cash Registers')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_CASH_REGISTER) && <Menu.Item key={`/app/system/pos-devices`}>{t('POS Devices')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_USER_MANAGER) && <Menu.Item key={`/app/system/roles`} icon={<UserIcon />}>{t('Roles')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.FIRM_USER_MANAGER) && <Menu.Item key={`/app/system/users`} icon={<UserIcon />}>{t('Users')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.APPLOG_VIEW) && <Menu.Item key={`/app/applog/grid`}>{t('Logs')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.APPLOG_VIEW) && <Menu.Item key={`/app/sms-logs/grid`}>{t('SMS Logs')}</Menu.Item>}
                            {hasPermission(PERMISSIONS.ENABIZ_OPERATION_TRACKING) && <Menu.Item key={`/app/system/enabiz-operations`}>{t('Enabiz Operation Tracking')}</Menu.Item>}
                            {<Menu.Item key={`/app/patient-queue/grid`} icon={<Patient />}>{t('Patient Queue')}</Menu.Item>}
                        </SubMenu>
                    )
                }
            </>
        )
    }

    const getMobileMenu = () => {
        return (
            <Menu
                style={{ marginLeft: 16, minWidth: 128 }}
                onClick={(item) => {
                    history.push(item.key)
                }}
                theme="dark"
                defaultSelectedKeys={[key]}
            >
                {getMenu()}
            </Menu>
        )
    }

    return (
        <Layout>
            <MobileView>
                <Header style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    height: 64,
                    padding: '0 16px',
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    borderBottom: '1px solid rgba(0,0,0,0.03)',
                    color: '#fff'
                }}>
                    <Flex alignItems={'center'} flexWrap={'nowrap'} justifyContent={'space-between'} style={{ width: '100%', height: '100%' }}>
                        <Button
                            type="text"
                            shape="circle"
                            icon={<MenuOutlined style={{ color: textColor, fontSize: '20px' }} />}
                            onClick={() => setMenuVisible(true)}
                            style={{ height: 40, width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <Logo />
                        </div>
                        <ProfileDropdown isMobile={true} textColor={textColor} />
                    </Flex>
                </Header>
                <Drawer
                    title={<Logo />}
                    placement="left"
                    closable={true}
                    onClose={() => setMenuVisible(false)}
                    visible={menuVisible}
                    bodyStyle={{ padding: 0 }}
                >
                    <Menu
                        mode="inline"
                        theme="light"
                        onClick={(item) => {
                            history.push(item.key)
                            setMenuVisible(false)
                        }}
                        defaultSelectedKeys={[key]}
                    >
                        {getMenu()}
                        <Menu.Item key={`/app/changelog-history`} icon={<HistoryOutlined />}>{t('Version History')}</Menu.Item>
                    </Menu>
                </Drawer>
            </MobileView>
            <BrowserView>
                <Header style={{
                    position: 'fixed',
                    zIndex: 100,
                    width: '100%',
                    height: 64,
                    backgroundColor: headerColor,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    borderBottom: '1px solid rgba(0,0,0,0.03)'
                }}>
                    <Flex alignItems={'center'}>
                        <Logo />
                        <Menu
                            onClick={(item) => {
                                history.push(item.key)
                            }}
                            theme={menuTheme}
                            mode="horizontal"
                            defaultSelectedKeys={[key]}
                            style={{ marginLeft: 16, flex: 1, background: 'transparent', borderBottom: 'none', color: textColor }}
                        >
                            {getMenu()}
                        </Menu>
                        <Button
                            type="text"
                            icon={<HistoryOutlined style={{ fontSize: '18px', color: textColor }} />}
                            onClick={() => history.push('/app/changelog-history')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 8
                            }}
                            title={t('Version History')}
                        />
                        <NotificationMenu />
                        <ProfileDropdown textColor={textColor} />
                    </Flex>
                </Header>
            </BrowserView>
            <Layout className="site-layout">
                {children}
                <Footer />
            </Layout>
            <AiChatButton />
        </Layout>
    )
}
